import createSlug from "../utils/createSlug";
import { TalentCategoryContract } from "../types/talents";

export interface ResearchCategoryConfig {
  id: number;
  slug: string;
  title: string;
  icon: string;
  description?: string;
}

const DEFAULT_ICON = "/assets/images/icons/info.png";
const DEFAULT_DESCRIPTION = "Discover upgrades in this research category.";

const CONFIGS: ResearchCategoryConfig[] = [
  {
    id: 1,
    slug: "loot-hunt",
    title: "Loot Hunt",
    icon: "/assets/images/adventure-icons/treasure.png",
    description: "Enhance your treasure hunting abilities",
  },
  {
    id: 2,
    slug: "buildcraft",
    title: "Buildcraft",
    icon: "/assets/images/castle.png",
    description: "Improve your construction and building",
  },
  {
    id: 3,
    slug: "military",
    title: "Military",
    icon: "/assets/images/adventure-icons/arena.png",
    description: "Strengthen your military forces",
  },
  {
    id: 4,
    slug: "wealth-trade",
    title: "Wealth & Trade",
    icon: "/assets/images/icons/cleg-coin.png",
    description: "Boost your economy and trading",
  },
];

export const RESEARCH_CATEGORIES = CONFIGS;

const idMap = new Map<number, ResearchCategoryConfig>();
const slugMap = new Map<string, ResearchCategoryConfig>();

CONFIGS.forEach((config) => {
  idMap.set(config.id, config);
  slugMap.set(config.slug.toLowerCase(), config);
});

const createFallbackConfig = (
  categoryId: number,
  name?: string
): ResearchCategoryConfig => ({
  id: categoryId,
  slug: createSlug(name ?? `category-${categoryId}`),
  title: name ?? `Category ${categoryId}`,
  icon: DEFAULT_ICON,
  description: DEFAULT_DESCRIPTION,
});

export const getResearchCategoryConfig = (
  categoryId: number,
  name?: string
): ResearchCategoryConfig => {
  return idMap.get(categoryId) ?? createFallbackConfig(categoryId, name);
};

export const getResearchCategoryBySlug = (
  slug: string
): ResearchCategoryConfig | undefined => {
  return slugMap.get(slug.toLowerCase());
};

export const getResearchCategorySlug = (
  category: Pick<TalentCategoryContract, "id" | "name"> | ResearchCategoryConfig
): string => {
  if ("slug" in category && category.slug) {
    return category.slug.toLowerCase();
  }

  const baseName =
    "name" in category
      ? category.name
      : "title" in category
      ? category.title
      : undefined;

  if (baseName) {
    return createSlug(baseName);
  }

  const fallback = getResearchCategoryConfig(category.id);
  return fallback.slug;
};

export const getResearchCategoryIcon = (
  category?:
    | Pick<TalentCategoryContract, "id" | "image">
    | ResearchCategoryConfig
): string => {
  if (!category) return DEFAULT_ICON;
  if ("image" in category && category.image) {
    return category.image;
  }
  if ("icon" in category && category.icon) {
    return category.icon;
  }
  if ("id" in category) {
    const fallback = getResearchCategoryConfig(category.id);
    return fallback.icon || DEFAULT_ICON;
  }
  return DEFAULT_ICON;
};

export const getResearchCategoryDescription = (
  category?:
    | Pick<TalentCategoryContract, "id" | "description" | "name">
    | ResearchCategoryConfig
): string => {
  if (!category) return DEFAULT_DESCRIPTION;
  if ("description" in category && category.description) {
    return category.description;
  }
  if ("id" in category) {
    const fallback = idMap.get(category.id);
    if (fallback?.description) {
      return fallback.description;
    }
  }
  return DEFAULT_DESCRIPTION;
};

export const findResearchCategoryBySlug = (
  categories: TalentCategoryContract[],
  slug?: string
): TalentCategoryContract | undefined => {
  if (!slug) return undefined;

  const numericId = Number(slug);
  if (!Number.isNaN(numericId)) {
    const numericMatch = categories.find((cat) => cat.id === numericId);
    if (numericMatch) return numericMatch;
  }

  const normalizedSlug = slug.toLowerCase();
  const configMatch = getResearchCategoryBySlug(normalizedSlug);
  if (configMatch) {
    const byId = categories.find((cat) => cat.id === configMatch.id);
    if (byId) return byId;
  }

  return categories.find(
    (cat) => getResearchCategorySlug(cat) === normalizedSlug
  );
};

export const resolveResearchCategoryId = (
  value?: string
): number | undefined => {
  if (!value) return undefined;

  const numeric = Number(value);
  if (!Number.isNaN(numeric) && numeric > 0) {
    return numeric;
  }

  const slugMatch = getResearchCategoryBySlug(value.toLowerCase());
  return slugMatch?.id;
};
